import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../api';

// Payouts tab of the publisher dashboard: Stripe Connect (Express) onboarding,
// what is currently owed, and payout history.
//
// WallPass never sees or stores publisher bank details — Stripe collects them in
// its own hosted onboarding flow, and this tab only ever holds an opaque account
// state plus the amounts we computed ourselves.

const PAGE_SIZE = 10;

// Filled portion of the progress meter. Brand gold stepped into the dark
// lightness band, matching the dashboard's existing validated chart marks
// (#F5A623 raw is too light for fills on the dark surface).
const METER_FILL = '#c98500';
const METER_TRACK = '#4d4b47';

const fmtUsd = (n) =>
  typeof n === 'number' ? n.toLocaleString('en-US', { style: 'currency', currency: 'USD' }) : '—';
const fmtInt = (n) => (typeof n === 'number' ? n.toLocaleString('en-US') : '—');
const fmtDate = (iso) =>
  iso ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';

const Card = ({ children, className = '' }) => (
  <div className={`bg-wp-surface rounded-xl border border-wp-border p-5 ${className}`}>{children}</div>
);

const SectionTitle = ({ children, sub }) => (
  <div className="mb-4">
    <h3 className="text-white text-lg font-semibold">{children}</h3>
    {sub && <p className="text-wp-muted text-sm mt-0.5">{sub}</p>}
  </div>
);

const StatTile = ({ label, value, sub }) => (
  <Card>
    <p className="text-wp-muted text-sm">{label}</p>
    <p className="text-white text-2xl font-semibold mt-1">{value}</p>
    {sub && <p className="text-wp-muted text-xs mt-1">{sub}</p>}
  </Card>
);

/**
 * Status is never carried by color alone — every state ships an icon and a word.
 * That is what keeps it readable for colorblind users, in forced-colors mode and
 * in print.
 */
const StatusPill = ({ tone, icon, children }) => {
  const tones = {
    good: 'bg-wp-success/20 text-wp-success',
    warning: 'bg-wp-accent/20 text-wp-accent',
    critical: 'bg-wp-danger/20 text-wp-danger',
    neutral: 'bg-wp-border text-wp-muted',
  };
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold tracking-wide ${tones[tone]}`}
    >
      <span aria-hidden="true">{icon}</span>
      {children}
    </span>
  );
};

/** Single-magnitude-toward-target meter. One series, so no legend — the labels name it. */
const ProgressToMinimum = ({ netUsd, minimumUsd }) => {
  const pct = minimumUsd > 0 ? Math.min((netUsd / minimumUsd) * 100, 100) : 0;
  return (
    <div>
      <div
        className="w-full h-2.5 rounded-full overflow-hidden"
        style={{ backgroundColor: METER_TRACK }}
        role="progressbar"
        aria-valuenow={netUsd}
        aria-valuemin={0}
        aria-valuemax={minimumUsd}
        aria-label={`${fmtUsd(netUsd)} of the ${fmtUsd(minimumUsd)} payout minimum`}
      >
        <div
          className="h-full rounded-full transition-all"
          style={{ width: `${pct}%`, backgroundColor: METER_FILL }}
        />
      </div>
      <div className="flex justify-between text-xs text-wp-muted mt-2">
        <span>{fmtUsd(netUsd)} earned</span>
        <span>{fmtUsd(minimumUsd)} minimum</span>
      </div>
    </div>
  );
};

const ConnectCard = ({ status, onConnect, isConnecting, error }) => {
  const { connect, terms } = status;

  let pill;
  let body;
  let cta = null;

  if (!connect.connected) {
    pill = <StatusPill tone="neutral" icon="○">NOT SET UP</StatusPill>;
    body = (
      <p className="text-sm text-wp-muted">
        Set up payouts to receive your earnings. Stripe collects your business and bank details
        directly — WallPass never sees or stores them. Takes a few minutes.
      </p>
    );
    cta = 'Set up payouts';
  } else if (connect.payoutsEnabled) {
    pill = <StatusPill tone="good" icon="✓">PAYOUTS ACTIVE</StatusPill>;
    body = (
      <p className="text-sm text-wp-muted">
        Your account is verified. Payouts run {terms.schedule}, and anything below the{' '}
        {fmtUsd(terms.minimumPayoutUsd)} minimum rolls over to the next one.
      </p>
    );
    cta = 'Update payout details';
  } else if (connect.detailsSubmitted) {
    pill = <StatusPill tone="warning" icon="⏳">VERIFYING</StatusPill>;
    body = (
      <div className="text-sm text-wp-muted space-y-2">
        <p>
          Stripe is verifying your details. This usually completes within a day; you will start
          receiving payouts automatically once it does.
        </p>
        {connect.requirementsDue.length > 0 && (
          <div>
            <p className="text-wp-accent">Stripe still needs:</p>
            <ul className="list-disc list-inside mt-1">
              {connect.requirementsDue.map((r) => (
                <li key={r}>{r.replace(/[._]/g, ' ')}</li>
              ))}
            </ul>
          </div>
        )}
      </div>
    );
    cta = 'Continue verification';
  } else {
    pill = <StatusPill tone="warning" icon="⏳">INCOMPLETE</StatusPill>;
    body = (
      <p className="text-sm text-wp-muted">
        Your payout setup was started but not finished. Pick up where you left off — nothing is lost.
      </p>
    );
    cta = 'Finish setup';
  }

  return (
    <Card>
      <div className="flex items-start justify-between gap-4 mb-3">
        <h3 className="text-white text-lg font-semibold">Payout account</h3>
        {pill}
      </div>
      {body}
      {connect.disabledReason && (
        <p className="text-wp-danger text-sm mt-3">
          Payouts are paused: {connect.disabledReason.replace(/[._]/g, ' ')}
        </p>
      )}
      {error && <p className="text-wp-danger text-sm mt-3">{error}</p>}
      {cta && (
        <button
          onClick={onConnect}
          disabled={isConnecting}
          className="mt-4 bg-wp-accent text-wp-bg font-bold px-5 py-2.5 rounded-lg text-sm hover:opacity-90 transition-opacity disabled:opacity-50"
        >
          {isConnecting ? 'Opening Stripe…' : cta}
        </button>
      )}
    </Card>
  );
};

const PayoutHistory = ({ items, total, page, setPage }) => {
  const pageCount = Math.max(Math.ceil(total / PAGE_SIZE), 1);

  if (total === 0) {
    return (
      <Card>
        <SectionTitle sub="Payouts appear here once your first one is sent.">History</SectionTitle>
        <p className="text-wp-muted text-sm">No payouts yet.</p>
      </Card>
    );
  }

  return (
    <Card>
      <SectionTitle sub="Every payout we have sent you, newest first.">History</SectionTitle>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-wp-muted text-left border-b border-wp-border">
              <th className="py-2 pr-4 font-medium">Date</th>
              <th className="py-2 pr-4 font-medium text-right">Tokens</th>
              <th className="py-2 pr-4 font-medium text-right">Gross</th>
              <th className="py-2 pr-4 font-medium text-right">Fee</th>
              <th className="py-2 pr-4 font-medium text-right">Paid to you</th>
              <th className="py-2 font-medium">Status</th>
            </tr>
          </thead>
          <tbody>
            {items.map((p) => (
              <tr key={p.id} className="border-b border-wp-border/50 last:border-0">
                <td className="py-2.5 pr-4 text-white">{fmtDate(p.paidAt || p.createdAt)}</td>
                <td className="py-2.5 pr-4 text-right text-wp-muted">{fmtInt(p.tokensSettled)}</td>
                <td className="py-2.5 pr-4 text-right text-wp-muted">{fmtUsd(p.grossUsd)}</td>
                <td className="py-2.5 pr-4 text-right text-wp-muted">
                  −{fmtUsd(p.platformFeeUsd)}
                  <span className="text-xs"> ({p.platformFeePercent}%)</span>
                </td>
                <td className="py-2.5 pr-4 text-right text-white font-semibold">{fmtUsd(p.netUsd)}</td>
                <td className="py-2.5">
                  {p.status === 'paid' && <StatusPill tone="good" icon="✓">PAID</StatusPill>}
                  {p.status === 'pending' && <StatusPill tone="warning" icon="⏳">PENDING</StatusPill>}
                  {p.status === 'failed' && (
                    <span title={p.failureReason || undefined}>
                      <StatusPill tone="critical" icon="!">FAILED</StatusPill>
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {pageCount > 1 && (
        <div className="flex items-center justify-between mt-4 text-sm">
          <button
            onClick={() => setPage(page - 1)}
            disabled={page <= 1}
            className="text-wp-muted hover:text-white disabled:opacity-40 transition-colors"
          >
            ← Previous
          </button>
          <span className="text-wp-muted">
            Page {page} of {pageCount}
          </span>
          <button
            onClick={() => setPage(page + 1)}
            disabled={page >= pageCount}
            className="text-wp-muted hover:text-white disabled:opacity-40 transition-colors"
          >
            Next →
          </button>
        </div>
      )}
    </Card>
  );
};

const PublisherPayoutsTab = () => {
  const [status, setStatus] = useState(null);
  const [payouts, setPayouts] = useState({ items: [], total: 0 });
  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [isConnecting, setIsConnecting] = useState(false);
  const [error, setError] = useState('');
  const [connectError, setConnectError] = useState('');
  const [searchParams, setSearchParams] = useSearchParams();

  const fetchAll = useCallback(async (pageNum) => {
    try {
      const [statusRes, payoutsRes] = await Promise.all([
        api.get('/publisher-dashboard/me/connect/status'),
        api.get(`/publisher-dashboard/me/payouts?page=${pageNum}&limit=${PAGE_SIZE}`),
      ]);
      setStatus(statusRes.data);
      setPayouts(payoutsRes.data);
      setError('');
    } catch (err) {
      setError(err.response?.data?.message || 'Could not load your payout information.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll(page);
  }, [fetchAll, page]);

  // Stripe sends the publisher back here after hosted onboarding. The status
  // endpoint reads live from Stripe, so a plain refetch is enough — we do not
  // wait on the account.updated webhook to render the right state.
  useEffect(() => {
    if (searchParams.get('connect')) {
      const next = new URLSearchParams(searchParams);
      next.delete('connect');
      setSearchParams(next, { replace: true });
      fetchAll(1);
    }
  }, [searchParams, setSearchParams, fetchAll]);

  const handleConnect = async () => {
    setConnectError('');
    setIsConnecting(true);
    try {
      const res = await api.post('/publisher-dashboard/me/connect/onboard');
      // Full-page navigation, not a popup: Stripe's onboarding is a multi-step
      // flow that can bounce to a bank's own verification, and it returns us to
      // the dashboard itself.
      window.location.assign(res.data.url);
    } catch (err) {
      setConnectError(err.response?.data?.message || 'Could not open Stripe onboarding.');
      setIsConnecting(false);
    }
  };

  if (isLoading) return <p className="text-wp-muted">Loading payouts…</p>;
  if (error) return <p className="text-wp-danger text-sm">{error}</p>;
  if (!status) return null;

  const { outstanding, terms } = status;
  const lastPaid = payouts.items.find((p) => p.status === 'paid');

  return (
    <div className="space-y-6">
      <ConnectCard
        status={status}
        onConnect={handleConnect}
        isConnecting={isConnecting}
        error={connectError}
      />

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <StatTile
          label="Pending balance"
          value={fmtUsd(outstanding.netUsd)}
          sub={`${fmtInt(outstanding.tokens)} tokens, after the ${terms.platformFeePercent}% platform fee`}
        />
        <StatTile
          label="Payout schedule"
          value={terms.schedule === 'monthly' ? 'Monthly' : terms.schedule}
          sub={`${fmtUsd(terms.minimumPayoutUsd)} minimum, rolls over below that`}
        />
        <StatTile
          label="Last payout"
          value={lastPaid ? fmtUsd(lastPaid.netUsd) : '—'}
          sub={lastPaid ? fmtDate(lastPaid.paidAt) : 'No payouts sent yet'}
        />
      </div>

      {!outstanding.meetsMinimum && outstanding.netUsd > 0 && (
        <Card>
          <SectionTitle sub="Earnings below the minimum carry forward — nothing is lost or deducted.">
            Progress to your next payout
          </SectionTitle>
          <ProgressToMinimum netUsd={outstanding.netUsd} minimumUsd={terms.minimumPayoutUsd} />
        </Card>
      )}

      {outstanding.meetsMinimum && (
        <Card>
          <p className="text-sm text-white">
            <span aria-hidden="true">✓</span> Your balance of{' '}
            <strong>{fmtUsd(outstanding.netUsd)}</strong> is above the{' '}
            {fmtUsd(terms.minimumPayoutUsd)} minimum and will be sent in the next monthly payout run.
          </p>
        </Card>
      )}

      <PayoutHistory
        items={payouts.items}
        total={payouts.total}
        page={page}
        setPage={setPage}
      />
    </div>
  );
};

export default PublisherPayoutsTab;
